// src/pages/Parents.js
//
// SOURCES DE DONNÉES (3) :
//   1. registrations/  — parents qui se sont inscrits sur le site web
//   2. users/ (role=Parent) — parents inscrits directement via l'app
//   3. parents/ — documents app avec children[] et linkRequests[]
//
// Les trois sources sont fusionnées par email.
// Les parents "app seulement" (jamais passés par le site) apparaissent maintenant.
//
// DESIGN : cartes compactes + bouton "Gérer les liaisons" toujours visible
// pour les parents ayant un compte app.

import React, { useState, useEffect, useMemo } from 'react';
import { db } from '../firebase';
import {
  collection, getDocs, query, orderBy, where
} from 'firebase/firestore';
import {
  Box, Typography, TextField, InputAdornment, Chip,
  Select, MenuItem, FormControl, InputLabel, Button,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Avatar, Grid, Card, CardActionArea,
  Divider, Alert, CircularProgress
} from '@mui/material';
import {
  Search as SearchIcon,
  FamilyRestroom as FamilyIcon,
  PhoneAndroid as AppIcon,
  Link as LinkIcon,
  HourglassTop as PendingIcon,
  CheckCircle as CheckIcon,
  FileDownload as DownloadIcon,
  Add as AddIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { CSVLink } from 'react-csv';
import moment from 'moment';

const PURPLE = '#7C3AED';

// ═══════════════════════════════════════════════════════════════════
// COMPOSANT PRINCIPAL
// ═══════════════════════════════════════════════════════════════════

export default function Parents() {
  const [parents,   setParents]   = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [search,    setSearch]    = useState('');
  const [filterCourse, setFilterCourse] = useState('');
  const [courses,   setCourses]   = useState([]);
  const [selected,  setSelected]  = useState(null);
  const navigate = useNavigate();

  useEffect(() => { loadAll(); }, []);

  // ─── Chargement des 3 sources ─────────────────────────────────────
  const loadAll = async () => {
    setLoading(true);
    try {
      // SOURCE 1 : inscriptions site web
      const regsSnap = await getDocs(
        query(collection(db, 'registrations'), orderBy('createdAt', 'desc'))
      );

      // SOURCE 2 : utilisateurs app avec rôle Parent
      const appUsersSnap = await getDocs(
        query(collection(db, 'users'), where('role', '==', 'Parent'))
      );

      // SOURCE 3 : collection parents/ (données app + children + linkRequests)
      const parentsSnap = await getDocs(collection(db, 'parents'));

      // Index email → appUser (uid)
      // Seuls les parents avec registrationSource='mobile_app' ou emailVerificationRequired=true
      // sont de VRAIS comptes app. Les autres viennent du site web ou de l'admin.
      const appByEmail = new Map();
      for (const d of appUsersSnap.docs) {
        const data = d.data();
        if (!data.email) continue;
        // registrationSource='mobile_app' : écrit par registration_form.dart (email)
        // et home_page.dart (Google). emailVerificationRequired couvre les anciens.
        // pseudo rempli = inscrit via app (Google ou email via Flutter)
        const isRealApp = (data.pseudo && data.pseudo.trim() !== '') ||
                          data.registrationSource === 'mobile_app' ||
                          data.emailVerificationRequired === true;
        if (isRealApp) {
          appByEmail.set(data.email.toLowerCase(), { uid: d.id, ...data });
        }
      }

      // Index uid → parentDoc et email → parentDoc (app seulement)
      const parentDocByUid   = new Map();
      const parentDocByEmail = new Map();
      for (const d of parentsSnap.docs) {
        const data = d.data();
        parentDocByUid.set(d.id, { id: d.id, ...data });
        // On n'indexe dans parentDocByEmail que les vrais comptes app
        const isRealApp = (data.pseudo && data.pseudo.trim() !== '') ||
                          data.registrationSource === 'mobile_app' ||
                          data.emailVerificationRequired === true;
        if (data.email && isRealApp) {
          parentDocByEmail.set(data.email.toLowerCase(), { id: d.id, ...data });
        }
      }

      const map = new Map(); // clé = email (ou uid si pas d'email)
      const coursesSet = new Set();

      // ── Traiter les inscriptions site web ──────────────────────────
      for (const regDoc of regsSnap.docs) {
        const reg = regDoc.data();
        const email =
          reg.parentInfo?.email || reg.contactInfo?.email || reg.guardianEmail;
        if (!email) continue;
        const key = email.toLowerCase();

        if (!map.has(key)) {
          const appUser   = appByEmail.get(key);
          const parentDoc = parentDocByEmail.get(key);
          const appUid    = appUser?.uid || parentDoc?.id || null;
          const pDoc      = parentDoc || parentDocByUid.get(appUid);

          map.set(key, {
            id:          regDoc.id,
            email,
            phone:       reg.parentInfo?.phone || reg.contactInfo?.phone || '',
            firstName:   reg.parentInfo?.parentFirstName || reg.contactInfo?.firstName || '',
            lastName:    reg.parentInfo?.parentLastName  || reg.contactInfo?.lastName  || '',
            createdAt:   reg.createdAt,
            children:    [],
            registrations: [],
            appUid,
            pendingRequests:    pDoc ? (pDoc.linkRequests || []).filter(r => r.status === 'pending').length : 0,
            linkedAppChildren:  pDoc?.children || [],
          });
        }

        const parent = map.get(key);
        parent.registrations.push({ id: regDoc.id, ...reg });

        for (const p of (reg.persons || [])) {
          if (!p.dateOfBirth) continue;
          const cKey = `${p.firstName}-${p.lastName}-${p.dateOfBirth}`.toLowerCase();
          if (!parent.children.find(c => c.id === cKey)) {
            parent.children.push({
              id: cKey,
              firstName: p.firstName,
              lastName:  p.lastName,
              dateOfBirth: p.dateOfBirth,
              course: reg.courseTitle || '',
              plan:   reg.planName   || '',
            });
            if (reg.courseTitle) coursesSet.add(reg.courseTitle);
          }
        }
      }

      // ── Ajouter les parents APP SEULEMENT (jamais passés par le site) ──
      // Ne garder que les vrais comptes app (registrationSource ou emailVerificationRequired)
      for (const [uid, pDoc] of parentDocByUid) {
        const isRealApp = (pDoc.pseudo && pDoc.pseudo.trim() !== '') ||
                          pDoc.registrationSource === 'mobile_app' ||
                          pDoc.emailVerificationRequired === true;
        if (!isRealApp) continue; // ignorer les parents synchro automatique (Users.js)

        const email = pDoc.email?.toLowerCase();
        const mapKey = email || uid;
        if (map.has(mapKey)) continue; // déjà dans la map via registrations

        map.set(mapKey, {
          id:      uid,
          email:   pDoc.email || '',
          phone:   pDoc.phone || '',
          firstName: pDoc.parentFirstName || pDoc.firstName || '',
          lastName:  pDoc.parentLastName  || pDoc.lastName  || '',
          createdAt: pDoc.createdAt,
          children:  [],
          registrations: [],
          appUid:  uid,
          appOnly: true,
          pendingRequests:   (pDoc.linkRequests || []).filter(r => r.status === 'pending').length,
          linkedAppChildren: pDoc.children || [],
        });
      }

      setCourses(Array.from(coursesSet).filter(Boolean).sort());
      setParents(Array.from(map.values()));
    } catch (err) {
      console.error('Parents.loadAll:', err);
    }
    setLoading(false);
  };

  // ─── Filtrage ─────────────────────────────────────────────────────
  const filtered = useMemo(() => {
    let list = parents;
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(p =>
        p.firstName?.toLowerCase().includes(q) ||
        p.lastName?.toLowerCase().includes(q) ||
        p.email?.toLowerCase().includes(q) ||
        p.phone?.includes(search) ||
        p.children.some(c =>
          `${c.firstName} ${c.lastName}`.toLowerCase().includes(q)) ||
        p.linkedAppChildren.some(c =>
          `${c.firstName} ${c.lastName}`.toLowerCase().includes(q))
      );
    }
    if (filterCourse) {
      list = list.filter(p =>
        p.children.some(c => c.course === filterCourse));
    }
    return list;
  }, [parents, search, filterCourse]);

  // ─── Stats ────────────────────────────────────────────────────────
  const stats = useMemo(() => ({
    total:      parents.length,
    withApp:    parents.filter(p => p.appUid).length,
    children:   parents.reduce((t, p) => t + p.children.length, 0),
    pending:    parents.reduce((t, p) => t + p.pendingRequests, 0),
  }), [parents]);

  // ─── CSV ──────────────────────────────────────────────────────────
  const csvData = filtered.map(p => ({
    'Prénom':       p.firstName,
    'Nom':          p.lastName,
    'Email':        p.email,
    'Téléphone':    p.phone,
    'Compte app':   p.appUid ? 'Oui' : 'Non',
    'Enfants site': p.children.map(c => `${c.firstName} ${c.lastName}`).join('; '),
    'Enfants liés': p.linkedAppChildren.map(c =>
      `${c.firstName} ${c.lastName}${c.uid ? ' ✓' : ''}`).join('; '),
  }));

  // ─── Render ───────────────────────────────────────────────────────
  return (
    <Box sx={{ p: 3 }}>

      {/* En-tête */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={700} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <FamilyIcon sx={{ color: PURPLE }} />
            Gestion des parents
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Chip size="small" label={`${stats.total} parents`} sx={{ borderColor: PURPLE, color: PURPLE }} variant="outlined" />
            <Chip size="small" icon={<AppIcon style={{ fontSize: 13 }} />} label={`${stats.withApp} comptes app`} color="success" variant="outlined" />
            <Chip size="small" label={`${stats.children} enfants inscrits`} variant="outlined" />
            {stats.pending > 0 && (
              <Chip size="small" icon={<PendingIcon style={{ fontSize: 13 }} />}
                label={`${stats.pending} en attente`} color="warning" />
            )}
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <CSVLink
            data={csvData}
            filename={`parents-${moment().format('YYYYMMDD')}.csv`}
            style={{ textDecoration: 'none' }}
          >
            <Button variant="outlined" size="small" startIcon={<DownloadIcon />}>
              Exporter CSV
            </Button>
          </CSVLink>
          <Button
            variant="contained" size="small" startIcon={<AddIcon />}
            onClick={() => navigate('/users/new?role=Parent')}
            sx={{ bgcolor: PURPLE, '&:hover': { bgcolor: '#6D28D9' } }}
          >
            Ajouter un parent
          </Button>
        </Box>
      </Box>

      {/* Filtres */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <TextField
          size="small"
          placeholder="Rechercher un parent ou enfant…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{ minWidth: 260, flexGrow: 1 }}
        />
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Cours</InputLabel>
          <Select value={filterCourse} label="Cours" onChange={e => setFilterCourse(e.target.value)}>
            <MenuItem value="">Tous</MenuItem>
            {courses.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
          </Select>
        </FormControl>
      </Box>

      {/* Loader */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress sx={{ color: PURPLE }} />
        </Box>
      )}

      {/* Grille de cartes compactes */}
      {!loading && (
        <>
          {filtered.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 8, color: 'text.secondary' }}>
              <FamilyIcon sx={{ fontSize: 48, opacity: 0.3, mb: 1 }} />
              <Typography>Aucun parent trouvé</Typography>
            </Box>
          ) : (
            <Grid container spacing={2}>
              {filtered.map(parent => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={parent.id}>
                  <ParentCard
                    parent={parent}
                    onClick={() => setSelected(parent)}
                    onManage={() => navigate(`/parents/${parent.appUid}`)}
                  />
                </Grid>
              ))}
            </Grid>
          )}
        </>
      )}

      {/* Dialog détail */}
      {selected && (
        <ParentDetailDialog
          parent={selected}
          onClose={() => setSelected(null)}
          onManage={selected.appUid
            ? () => { setSelected(null); navigate(`/parents/${selected.appUid}`); }
            : null}
        />
      )}
    </Box>
  );
}

// ═══════════════════════════════════════════════════════════════════
// CARTE COMPACTE
// ═══════════════════════════════════════════════════════════════════

function ParentCard({ parent, onClick, onManage }) {
  const initials = `${parent.firstName?.[0] || ''}${parent.lastName?.[0] || ''}`.toUpperCase() || '?';
  const linkedCount  = (parent.linkedAppChildren || []).filter(c => c.uid).length;
  const pendingCount = parent.pendingRequests || 0;

  return (
    <Card
      variant="outlined"
      sx={{
        borderColor:  parent.appUid ? 'rgba(124,58,237,0.25)' : 'divider',
        transition:   'box-shadow 0.2s, border-color 0.2s',
        '&:hover':    { boxShadow: '0 4px 16px rgba(124,58,237,0.12)', borderColor: PURPLE },
        display:      'flex',
        flexDirection: 'column',
      }}
    >
      {/* Zone cliquable → ouvre le dialog */}
      <CardActionArea onClick={onClick} sx={{ p: 2, flexGrow: 1 }}>
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
          <Avatar
            sx={{
              bgcolor:  parent.appUid ? PURPLE : '#9CA3AF',
              width: 44, height: 44,
              fontSize: 16, fontWeight: 700,
              flexShrink: 0,
            }}
          >
            {initials}
          </Avatar>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography fontWeight={700} noWrap fontSize={14}>
              {parent.firstName} {parent.lastName}
            </Typography>
            <Typography variant="body2" color="text.secondary" noWrap fontSize={12}>
              {parent.email || '—'}
            </Typography>
            {parent.phone && (
              <Typography variant="body2" color="text.secondary" fontSize={11}>
                {parent.phone}
              </Typography>
            )}

            {/* Badges */}
            <Box sx={{ display: 'flex', gap: 0.5, mt: 1, flexWrap: 'wrap' }}>
              {parent.appUid && (
                <Chip
                  icon={<AppIcon style={{ fontSize: 11 }} />}
                  label="App"
                  size="small" color="success"
                  sx={{ height: 20, fontSize: 10, '& .MuiChip-label': { px: 0.6 } }}
                />
              )}
              {parent.appOnly && (
                <Chip
                  label="App uniquement"
                  size="small" color="secondary" variant="outlined"
                  sx={{ height: 20, fontSize: 10, '& .MuiChip-label': { px: 0.6 } }}
                />
              )}
              {parent.children.length > 0 && (
                <Chip
                  label={`${parent.children.length} enfant${parent.children.length > 1 ? 's' : ''}`}
                  size="small" variant="outlined"
                  sx={{ height: 20, fontSize: 10, '& .MuiChip-label': { px: 0.6 } }}
                />
              )}
              {linkedCount > 0 && (
                <Chip
                  icon={<CheckIcon style={{ fontSize: 11 }} />}
                  label={`${linkedCount} lié${linkedCount > 1 ? 's' : ''}`}
                  size="small" color="primary" variant="outlined"
                  sx={{ height: 20, fontSize: 10, '& .MuiChip-label': { px: 0.6 } }}
                />
              )}
              {pendingCount > 0 && (
                <Chip
                  icon={<PendingIcon style={{ fontSize: 11 }} />}
                  label={`${pendingCount} en attente`}
                  size="small" color="warning"
                  sx={{ height: 20, fontSize: 10, '& .MuiChip-label': { px: 0.6 } }}
                />
              )}
            </Box>
          </Box>
        </Box>
      </CardActionArea>

      {/* Bouton "Gérer les liaisons" — toujours visible si compte app */}
      {parent.appUid && (
        <Box sx={{ px: 1.5, pb: 1.5, pt: 0 }}>
          <Button
            fullWidth size="small"
            variant="contained"
            startIcon={<LinkIcon fontSize="small" />}
            onClick={e => { e.stopPropagation(); onManage(); }}
            sx={{
              bgcolor: PURPLE,
              '&:hover': { bgcolor: '#6D28D9' },
              fontSize: 12, py: 0.6,
              boxShadow: 'none',
            }}
          >
            Gérer les liaisons
          </Button>
        </Box>
      )}
    </Card>
  );
}

// ═══════════════════════════════════════════════════════════════════
// DIALOG DÉTAIL
// ═══════════════════════════════════════════════════════════════════

function ParentDetailDialog({ parent, onClose, onManage }) {
  const initials = `${parent.firstName?.[0] || ''}${parent.lastName?.[0] || ''}`.toUpperCase() || '?';

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Avatar sx={{ bgcolor: parent.appUid ? PURPLE : '#9CA3AF', width: 46, height: 46, fontWeight: 700 }}>
            {initials}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography fontWeight={700} fontSize={17}>
              {parent.firstName} {parent.lastName}
            </Typography>
            <Typography variant="body2" color="text.secondary" noWrap>{parent.email}</Typography>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'flex-end' }}>
            {parent.appUid
              ? <Chip icon={<AppIcon style={{ fontSize: 12 }} />} label="Compte app" color="success" size="small" />
              : <Chip label="Pas de compte app" size="small" variant="outlined" />
            }
            {parent.appOnly && (
              <Chip label="App uniquement" size="small" color="secondary" variant="outlined" />
            )}
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 2.5 }}>

        {/* Contact */}
        <Section title="Contact">
          <InfoRow label="Email" value={parent.email || '—'} />
          {parent.phone && <InfoRow label="Téléphone" value={parent.phone} />}
          {parent.appUid && (
            <InfoRow label="UID Firebase" value={parent.appUid} mono />
          )}
        </Section>

        {/* Alerte demandes en attente */}
        {parent.pendingRequests > 0 && (
          <Alert severity="warning" sx={{ mb: 2 }} icon={<PendingIcon />}>
            {parent.pendingRequests} demande{parent.pendingRequests > 1 ? 's' : ''} de liaison en attente
            {onManage && ' — cliquez sur "Gérer les liaisons" pour les traiter'}
          </Alert>
        )}

        {/* Enfants liés dans l'app */}
        {parent.linkedAppChildren?.length > 0 && (
          <Section title={`Enfants liés (app) — ${parent.linkedAppChildren.length}`}>
            {parent.linkedAppChildren.map((child, i) => (
              <Box key={i} sx={{
                display: 'flex', alignItems: 'center', gap: 1,
                p: 1.2, mb: 0.8, bgcolor: '#F0FDF4',
                borderRadius: 1.5, border: '1px solid #BBF7D0',
              }}>
                <Avatar sx={{ width: 30, height: 30, bgcolor: '#10B981', fontSize: 13 }}>
                  {child.firstName?.[0] || '?'}
                </Avatar>
                <Typography fontSize={13} fontWeight={600} sx={{ flex: 1 }}>
                  {child.firstName} {child.lastName}
                </Typography>
                {child.uid
                  ? <Chip label="Compte actif" size="small" color="success" sx={{ height: 18, fontSize: 10 }} />
                  : <Chip label="Sans compte" size="small" variant="outlined" sx={{ height: 18, fontSize: 10 }} />
                }
              </Box>
            ))}
          </Section>
        )}

        {/* Enfants inscrits sur le site */}
        {parent.children.length > 0 && (
          <Section title={`Enfants inscrits (site web) — ${parent.children.length}`}>
            {parent.children.map((child, i) => (
              <Box key={i} sx={{
                p: 1.2, mb: 0.8,
                bgcolor: 'grey.50', borderRadius: 1.5,
              }}>
                <Typography fontSize={13} fontWeight={600}>
                  {child.firstName} {child.lastName}
                </Typography>
                {child.dateOfBirth && (
                  <Typography variant="body2" color="text.secondary" fontSize={12}>
                    Né·e le {moment(child.dateOfBirth).isValid()
                      ? moment(child.dateOfBirth).format('DD/MM/YYYY')
                      : child.dateOfBirth}
                  </Typography>
                )}
                {child.course && (
                  <Typography variant="body2" color="text.secondary" fontSize={12}>
                    {child.course}{child.plan ? ` · ${child.plan}` : ''}
                  </Typography>
                )}
              </Box>
            ))}
          </Section>
        )}

        {/* Inscriptions */}
        {parent.registrations.length > 0 && (
          <Section title={`Inscriptions (${parent.registrations.length})`}>
            {parent.registrations.slice(0, 4).map((reg, i) => (
              <Box key={i} sx={{ p: 1, mb: 0.5, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography fontSize={12}>
                  <strong>{reg.courseTitle || 'Cours'}</strong>
                  {reg.planName && ` · ${reg.planName}`}
                  {reg.createdAt && ` · ${moment(reg.createdAt.toDate?.() || reg.createdAt).format('DD/MM/YY')}`}
                </Typography>
              </Box>
            ))}
            {parent.registrations.length > 4 && (
              <Typography variant="caption" color="text.secondary">
                +{parent.registrations.length - 4} inscription(s) supplémentaires
              </Typography>
            )}
          </Section>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Fermer</Button>
        {onManage && (
          <Button
            variant="contained"
            startIcon={<LinkIcon />}
            onClick={onManage}
            sx={{ bgcolor: PURPLE, '&:hover': { bgcolor: '#6D28D9' } }}
          >
            Gérer les liaisons
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

// ─── Helpers UI ──────────────────────────────────────────────────────

function Section({ title, children }) {
  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="caption" color="text.secondary" fontWeight={700}
        letterSpacing={0.8} sx={{ display: 'block', mb: 1, textTransform: 'uppercase' }}>
        {title}
      </Typography>
      {children}
      <Divider sx={{ mt: 2 }} />
    </Box>
  );
}

function InfoRow({ label, value, mono }) {
  return (
    <Box sx={{ display: 'flex', gap: 1, mb: 0.5 }}>
      <Typography variant="body2" color="text.secondary" sx={{ minWidth: 100, flexShrink: 0 }}>
        {label}
      </Typography>
      <Typography variant="body2" fontFamily={mono ? 'monospace' : undefined}
        fontSize={mono ? 11 : undefined}>
        {value}
      </Typography>
    </Box>
  );
}